import React, { useState, useEffect, useCallback } from 'react';
import './Jokes.css';

const DEFAULT_BATCH = 20;
const LOAD_MORE_THRESHOLD = 300;

const formatPrice = (value) => {
    return Number(value).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    });
};

function Jokes({ theme, onBackClick, onAddToCart }) {
    const [jokes, setJokes] = useState([]);
    const [visibleCount, setVisibleCount] = useState(DEFAULT_BATCH);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [quantityById, setQuantityById] = useState({});
    const [cartMessage, setCartMessage] = useState('');
    const [justAddedId, setJustAddedId] = useState(null);

    useEffect(() => {
        let cancelled = false;

        const loadJokes = async () => {
            try {
                const response = await fetch('/jokes.json');
                if (!response.ok) {
                    throw new Error(`Unable to load jokes: ${response.status}`);
                }
                const data = await response.json();
                if (!cancelled) {
                    setJokes(Array.isArray(data) ? data : []);
                    setVisibleCount(DEFAULT_BATCH);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error(err);
                    setError('Could not load jokes. Please try again later.');
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        loadJokes();
        return () => {
            cancelled = true;
        };
    }, []);

    const handleScroll = useCallback(() => {
        if (isLoading || jokes.length <= visibleCount) return;

        if (window.innerHeight + window.scrollY >= document.body.offsetHeight - LOAD_MORE_THRESHOLD) {
            setVisibleCount((prev) => Math.min(jokes.length, prev + DEFAULT_BATCH));
        }
    }, [isLoading, jokes.length, visibleCount]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    const visibleJokes = jokes.slice(0, visibleCount);

    const handleAddToCart = (joke, index, quantity) => {
        if (!onAddToCart) return;

        const normalizedQuantity = Math.max(1, Number(quantity) || 1);
        const product = {
            id: `joke-${index}`,
            name: `Programmer Joke Pack ${index + 1}`,
            desc: joke,
            img: '',
            price: 29 + index * 7,
        };

        onAddToCart(product, normalizedQuantity);
        setQuantityById((prev) => ({ ...prev, [product.id]: 1 }));
        setJustAddedId(product.id);
        setCartMessage(`Added ${normalizedQuantity} × ${product.name} to the cart.`);
        window.setTimeout(() => {
            setJustAddedId(null);
            setCartMessage('');
        }, 1400);
    };

    return (
        <div className={`app ${theme}`}>
            <main>
                <section className="jokes-header-section">
                    <div className="jokes-header">
                        <div>
                            <h1>Programmer Jokes</h1>
                            <p className="jokes-subtitle">
                                Some of the stupidest programming jokes I could find on the internet, just for you.
                                DONT BLAME ME IF YOU LOSE BRAIN CELLS READING THESE. Thanks.
                            </p>
                        </div>
                        <button className="back-btn" onClick={onBackClick}>
                            Back to Home
                        </button>
                    </div>
                </section>

                {cartMessage && (
                    <div className="cart-feedback">{cartMessage}</div>
                )}

                <section className="jokes-content">
                    {isLoading ? (
                        <div className="jokes-status">Loading jokes…</div>
                    ) : error ? (
                        <div className="jokes-status jokes-error">{error}</div>
                    ) : (
                        <>
                            <div className="jokes-list">
                                {visibleJokes.map((joke, index) => (
                                    <article key={`${index}-${joke.slice(0, 40)}`} className="joke-card">
                                        <span className="joke-index">{index + 1}.</span>
                                        <p>{joke}</p>
                                        <div className="product-footer">
                                            <span className="product-price">{formatPrice(29 + index * 7)}</span>
                                            <div className="product-actions">
                                                <label className="qty-label">
                                                    Qty
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={quantityById[`joke-${index}`] ?? 1}
                                                        onChange={(event) => {
                                                            const nextValue = Math.max(1, Number(event.target.value) || 1);
                                                            setQuantityById((prev) => ({ ...prev, [`joke-${index}`]: nextValue }));
                                                        }}
                                                    />
                                                </label>
                                                <button
                                                    type="button"
                                                    className={`add-cart-btn ${justAddedId === `joke-${index}` ? 'added' : ''}`}
                                                    onClick={() => handleAddToCart(joke, index, quantityById[`joke-${index}`] ?? 1)}
                                                >
                                                    {justAddedId === `joke-${index}` ? 'Added to cart' : 'Add to Cart'}
                                                </button>
                                            </div>
                                        </div>
                                    </article>
                                ))}
                            </div>

                            <div className="jokes-footer-note">
                                {visibleCount < jokes.length
                                    ? 'Scroll to load more jokes...'
                                    : 'You have read all the jokes for now.'}
                            </div>
                        </>
                    )}
                </section>
            </main>

            <footer className="footer">
                <p>© 2026 StupidDesigns.com. All rights reserved. Not that anyone actually wants them.</p>
            </footer>
        </div>
    );
}

export default Jokes;
